import { useState, useCallback, useRef, useEffect } from 'react';
import { Sparkles, X, ImagePlus, Wand2, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { smartSceneAnalyzer, type AnalysisResult } from '../ai/smartSceneAnalyzer';
import { usePromptArchitect } from '@/hooks/usePromptArchitect';
import PromptArchitectPanel from '@/components/PromptArchitectPanel';
import { buildSupabaseFunctionUrl, normalizeBaseUrl } from '@/lib/url';

export interface ImageAnalysisResult {
  intent: 'full_analysis' | 'style_reference' | 'character_reference' | 'face_transform' | 'face_swap';
  subject?: string;
  environment?: string;
  lighting?: string;
  camera?: string;
  style?: string;
  mood?: string;
  palette?: string;
  pose?: string;
  clothing?: string;
  faceDescription?: string;
  hair?: string;
  skinTone?: string;
  ageRange?: string;
  expression?: string;
  ethnicityHint?: string;
  transformation?: string;
}

interface Props {
  sceneDescription: string;
  onSceneChange: (s: string) => void;
  onCanvasUpdate?: (updates: Record<string, any>) => void;
  onRawTextSubmit?: (rawText: string) => void;
  graphPrompt?: string;
  onImageAnalysis?: (result: ImageAnalysisResult) => void;
}

interface UploadedImage {
  file: File;
  preview: string;
  base64: string;
  mimeType: string;
}

const SUPABASE_URL = normalizeBaseUrl(import.meta.env.VITE_SUPABASE_URL);
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

function fileToBase64(file: File): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      resolve({ base64, mimeType: file.type });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const mergeGraphContext = (base: string, graphPrompt: string): string => {
  const source = graphPrompt.trim();
  if (!source || source.startsWith('Place elements')) return base;

  // if the user has already provided some text, merge the full graph prompt as
  // a prefix so that subsequent refinements keep the original description
  // intact. otherwise fall back to the old "pick a few keywords" heuristic.
  if (base.trim()) {
    // avoid duplicating the same text twice
    if (base.includes(source) || source.includes(base)) return base;
    return `${source}, ${base}`;
  }

  const picks = [
    source.match(/\b\d{2,3}mm lens\b/i)?.[0],
    source.match(/\b(medium shot|wide shot|close-up|low angle|eye-level|top-down)\b/i)?.[0],
    source.match(/\b(sunlight|moonlight|low key lighting|natural daylight|volumetric light)\b/i)?.[0],
    source.match(/\b(16:9|21:9|rule of thirds|widescreen composition)\b/i)?.[0],
  ].filter(Boolean) as string[];

  if (!picks.length) return base;
  const additions = picks.filter((item) => !new RegExp(item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(base));
  if (!additions.length) return base;

  return `${base}, ${additions.join(', ')}`;
};

type DetailDirective = 'none' | 'reduce' | 'enhance';

const REDUCE_DETAIL_REGEX = /\b(قلل\s*التفاصيل|خفف\s*التفاصيل|اختصر|بسّط|بسط|less\s*detail|reduce\s*details?|simplify|make\s*it\s*short|shorter|brief)\b/i;
const ENHANCE_DETAIL_REGEX = /\b(زود\s*تفاصيل|جود|حسن|طور|enhance|improve|خلي\s*احسن|كمل|زود)\b/i;

const detectDetailDirective = (text: string): DetailDirective => {
  const clean = text.trim();
  if (!clean) return 'none';
  if (REDUCE_DETAIL_REGEX.test(clean)) return 'reduce';
  if (ENHANCE_DETAIL_REGEX.test(clean)) return 'enhance';
  return 'none';
};

const reducePromptLocally = (text: string): string => text
  .split(/[\n.,؛،]+/)
  .map((part) => part.trim())
  .filter(Boolean)
  .slice(0, 3)
  .join('. ')
  .replace(/\s{2,}/g, ' ')
  .trim();


const EXAMPLE_PROMPTS = [
  "بنت واقفة في شارع بالليل وفي مطر",
  "A warrior standing on a cliff at sunset",
  "واحد واقف في مكان زي blade runner",
  "Cat sitting on a rooftop, golden hour",
];

const STYLE_REF_REGEX = /\b(use this style|استخدم الستايل|نفس الستايل|same style|copy style|match style)\b/i;
const CHARACTER_REF_REGEX = /\b(put (?:his|her|this) face|حط وشه|حط وشها|face swap|face on)\b/i;
const TRANSFORM_REF_REGEX = /\b(put (?:this|him|her) in|change clothing|make (?:this|him|her) a|حطه في|حطها في|غير هدومه|البسه)\b/i;

export default function SmartSceneInput({ sceneDescription, onSceneChange, onCanvasUpdate, onRawTextSubmit, graphPrompt = '', onImageAnalysis }: Props) {
  const [inputText, setInputText] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [liveAnalysis, setLiveAnalysis] = useState<AnalysisResult | null>(null);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [showExamples, setShowExamples] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  // Conversational state: track the current built prompt for incremental edits
  const [conversationHistory, setConversationHistory] = useState<string[]>([]);
  const currentPromptRef = useRef<string>('');
  const abortRef = useRef<AbortController | null>(null);
  const liveAnalyzeTimerRef = useRef<number | null>(null);
  const lastAnalyzedTextRef = useRef('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // AI Prompt Architect hook
  const { result: architectResult, isProcessing: isArchitectProcessing, currentSceneState, analyze: architectAnalyzeDirect, analyzeDebounced: architectAnalyze, reset: architectReset } = usePromptArchitect();

  // Keep currentPromptRef in sync with sceneDescription
  useEffect(() => {
    if (sceneDescription?.trim()) {
      currentPromptRef.current = sceneDescription;
    }
  }, [sceneDescription]);


  const analyzeAndProcessScene = useCallback((description: string, options?: { syncPrompt?: boolean }) => {
    const clean = description.trim();
    if (!clean) {
      setLiveAnalysis(null);
      return null;
    }

    const analysis: AnalysisResult = smartSceneAnalyzer.analyzeScene(clean);
    const newPrompt = smartSceneAnalyzer.generatePrompt(analysis.elements);
    setLiveAnalysis(analysis);

    if (options?.syncPrompt !== false) {
      onSceneChange(newPrompt);
    }

    if (onCanvasUpdate) {
      const canvasUpdates = smartSceneAnalyzer.convertToCanvasUpdates(analysis.elements);
      onCanvasUpdate(canvasUpdates);
    }

    return analysis;
  }, [onSceneChange, onCanvasUpdate]);

  // Don't sync sceneDescription back to inputText — input should stay clear after sending

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputText]);

  // NO auto-apply of architect results — only explicit Generate/Enter triggers prompt updates.
  // NO debounced AI calls on keystroke — saves API calls and prevents abort race conditions.

  // Local live analysis for UI hints only (no prompt sync, no API calls)
  useEffect(() => {
    if (liveAnalyzeTimerRef.current) {
      window.clearTimeout(liveAnalyzeTimerRef.current);
      liveAnalyzeTimerRef.current = null;
    }

    const clean = inputText.trim();
    if (!clean) {
      lastAnalyzedTextRef.current = '';
      setLiveAnalysis(null);
      return;
    }

    if (images.length > 0 || isRefining) return;

    liveAnalyzeTimerRef.current = window.setTimeout(() => {
      if (clean === lastAnalyzedTextRef.current) return;
      analyzeAndProcessScene(clean, { syncPrompt: false });
      lastAnalyzedTextRef.current = clean;
    }, 450);

    return () => {
      if (liveAnalyzeTimerRef.current) {
        window.clearTimeout(liveAnalyzeTimerRef.current);
        liveAnalyzeTimerRef.current = null;
      }
    };
  }, [inputText, images.length, isRefining, analyzeAndProcessScene]);

  // core refinement routine must be declared before any callbacks that
  // reference it, otherwise JavaScript throws a TDZ error when the callbacks
  // are created during rendering. the function itself lives quite a bit lower
  // in the component, so we pull it up here and then reassign later via
  // `refineFn` once the original implementation is known.
  // (simpler than rearchitecting with refs.)

  const runRefine = useCallback(async (text: string, options?: { clearInputOnSuccess?: boolean; clearImagesOnSuccess?: boolean }) => {
    const normalizedText = text.trim();

    // ---------- special commands ----------
    // requests to just copy/analyze the image prompt
    const copyPattern = /\b(?:هات\s*برومبت\s*(?:الصورة)?|حللها|copy\s*prompt|prompt\s*image)\b/i;
    // instructions to modify the existing image prompt
    const editPattern = /\b(?:عدّل(?:ها|\s*فيها)?|غير(?:ها|\s*فيها)?|modify(?:\s*image)?|change(?:\s*image)?)\b/i;

    if (copyPattern.test(normalizedText) && graphPrompt) {
      onSceneChange(graphPrompt);
      if (options?.clearInputOnSuccess) setInputText('');
      setIsRefining(false);
      return;
    }

    if (editPattern.test(normalizedText) && graphPrompt) {
      // strip the keyword and treat the remainder as extra instructions
      const extra = normalizedText.replace(editPattern, '').trim();
      const merged = extra ? `${graphPrompt}, ${extra}` : graphPrompt;
      onSceneChange(merged);
      if (options?.clearInputOnSuccess) setInputText('');
      setIsRefining(false);
      return;
    }

    if (!normalizedText && images.length === 0) return;

    if (isRefining) return;

    setIsRefining(true);
    setStreamingText('');

    const controller = new AbortController();
    abortRef.current = controller;

    const detailDirective = detectDetailDirective(normalizedText);
    const existing = sceneDescription?.trim() || '';
    const mergedMessage = detailDirective === 'reduce'
      ? normalizedText
      : mergeGraphContext(normalizedText, graphPrompt);

    const fallbackPrompt =
      detailDirective === 'reduce' && existing
        ? reducePromptLocally(existing)
        : (normalizedText || existing);

    try {
      const imageData = images.map(img => ({
        base64: img.base64,
        mimeType: img.mimeType,
      }));
      const canUseRemote = Boolean(SUPABASE_URL && SUPABASE_KEY);

      // ─── Image Analysis Branch ───
      // If images are present and we have the callback, run structured analysis
      const shouldAnalyze = imageData.length > 0 && onImageAnalysis && canUseRemote;
      const isStyleRef = STYLE_REF_REGEX.test(normalizedText);
      const isCharRef = CHARACTER_REF_REGEX.test(normalizedText);
      const isTransform = TRANSFORM_REF_REGEX.test(normalizedText);
      const isImageOnly = imageData.length > 0 && !normalizedText;

      if (shouldAnalyze && (isStyleRef || isCharRef || isTransform || isImageOnly)) {
        setStreamingText('Analyzing image...');
        try {
          const analyzeResp = await fetch(buildSupabaseFunctionUrl(SUPABASE_URL, 'image-analyze'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${SUPABASE_KEY}`,
            },
            body: JSON.stringify({
              images: imageData,
              instruction: normalizedText || undefined,
            }),
            signal: controller.signal,
          });

          if (analyzeResp.ok) {
            const analysisResult: ImageAnalysisResult = await analyzeResp.json();
            onImageAnalysis(analysisResult);

            // Also build a text prompt from the analysis for the scene description
            const parts = [
              analysisResult.subject,
              analysisResult.environment,
              analysisResult.lighting,
              analysisResult.camera,
              analysisResult.style,
              analysisResult.mood,
              analysisResult.palette ? `${analysisResult.palette} palette` : '',
            ].filter(Boolean);

            if (analysisResult.intent === 'face_swap' || analysisResult.intent === 'face_transform') {
              const faceParts = [
                analysisResult.faceDescription ? `a person with ${analysisResult.faceDescription}` : analysisResult.subject,
                analysisResult.hair ? `${analysisResult.hair} hair` : '',
                analysisResult.transformation || '',
                analysisResult.environment,
                analysisResult.lighting,
              ].filter(Boolean);
              onSceneChange(faceParts.join(', '));
            } else {
              onSceneChange(parts.join(', '));
            }

            setStreamingText('');
            if (options?.clearInputOnSuccess) setInputText('');
            if (options?.clearImagesOnSuccess) {
              images.forEach(img => URL.revokeObjectURL(img.preview));
              setImages([]);
            }
            setIsRefining(false);
            return;
          } else if (analyzeResp.status === 429) {
            toast.error('Rate limit exceeded — please wait a moment and try again.');
            setIsRefining(false);
            return;
          } else if (analyzeResp.status === 402) {
            toast.error('AI credits exhausted — add credits in Settings → Workspace → Usage.');
            setIsRefining(false);
            return;
          }
          // If analysis fails, fall through to normal scene-refine
          console.warn('Image analysis failed, falling through to scene-refine');
        } catch (analyzeErr) {
          if (analyzeErr instanceof Error && analyzeErr.name === 'AbortError') throw analyzeErr;
          console.warn('Image analysis error, falling through:', analyzeErr);
        }
        setStreamingText('');
      }

      if (!canUseRemote) {
        onSceneChange(fallbackPrompt);
        if (options?.clearInputOnSuccess) setInputText('');
        setStreamingText('');
        return;
      }

      const resp = await fetch(buildSupabaseFunctionUrl(SUPABASE_URL, 'scene-refine'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_KEY}`,
        },
        body: JSON.stringify({
          message: mergedMessage || 'Describe this image as a cinematic scene',
          images: imageData.length > 0 ? imageData : undefined,
          existingPrompt: existing || undefined,
          detailDirective,
        }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
        if (resp.status === 429) {
          toast.error('Rate limit exceeded — please wait a moment and try again.');
        } else if (resp.status === 402) {
          toast.error('AI credits exhausted — add credits in Settings → Workspace → Usage.');
        } else {
          onSceneChange(fallbackPrompt);
          setIsRefining(false);
          return;
        }
        setIsRefining(false);
        return;
      }

      if (!resp.body) throw new Error('No stream body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let newlineIdx: number;
        while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIdx);
          buffer = buffer.slice(newlineIdx + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              accumulated += content;
              setStreamingText(accumulated);
            }
          } catch {
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      if (buffer.trim()) {
        for (let raw of buffer.split('\n')) {
          if (!raw) continue;
          if (raw.endsWith('\r')) raw = raw.slice(0, -1);
          if (raw.startsWith(':') || raw.trim() === '') continue;
          if (!raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              accumulated += content;
              setStreamingText(accumulated);
            }
          } catch { /* ignore */ }
        }
      }

      if (accumulated.trim()) {
        // Pass the AI result directly without extra enhancement pipeline
        onSceneChange(accumulated.trim());
        if (options?.clearInputOnSuccess) {
          setInputText('');
        }
        setStreamingText('');
        if (options?.clearImagesOnSuccess) {
          images.forEach(img => URL.revokeObjectURL(img.preview));
          setImages([]);
        }
      }
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') {
        // User cancelled
      } else {
        console.error('Scene refine error:', e);
        onSceneChange(fallbackPrompt);
      }
    } finally {
      setIsRefining(false);
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, [graphPrompt, images, isRefining, onSceneChange, sceneDescription]);

  
  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const maxImages = 4;
    const maxSize = 10 * 1024 * 1024;

    for (const file of Array.from(files)) {
      if (images.length >= maxImages) {
        toast.error(`Maximum ${maxImages} images allowed`);
        break;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      try {
        const { base64, mimeType } = await fileToBase64(file);
        const preview = URL.createObjectURL(file);
        setImages(prev => [...prev, { file, preview, base64, mimeType }]);
      } catch {
        toast.error(`Failed to process ${file.name}`);
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = '';

    // if the user dropped/selected images and there is no manual prompt text,
    // immediately run the refine pipeline so that the initial scene description
    // reflects the image content. this makes the "prompt box" auto‑understand
    // uploaded references.
    if (!inputText.trim() && images.length + files.length > 0) {
      setTimeout(() => {
        runRefine('', { clearInputOnSuccess: false, clearImagesOnSuccess: false }).catch(() => {});
      }, 0);
    }
  }, [images.length, inputText, runRefine]);

  const removeImage = useCallback((index: number) => {
    setImages(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);




  const handleRefine = useCallback(async () => {
    const text = inputText.trim();
    if (!text && images.length === 0) return;

    // For text-only input, use the AI Prompt Architect with conversational context
    if (text && images.length === 0) {
      // Fire raw text sync for settings detection BEFORE AI processing
      onRawTextSubmit?.(text);
      setIsRefining(true);
      setStreamingText('');

      const existingPrompt = currentPromptRef.current || '';
      const isFollowUp = Boolean(existingPrompt);

      try {
        setStreamingText(isFollowUp ? 'Updating scene...' : 'Analyzing scene...');
        const result = await architectAnalyzeDirect(text, existingPrompt || undefined);

        if (result?.prompt) {
          onSceneChange(result.prompt);
          currentPromptRef.current = result.prompt;
          setConversationHistory(prev => [...prev, text]);
          setInputText('');
          lastAnalyzedTextRef.current = text;
        } else {
          // Fallback to local smart analysis
          analyzeAndProcessScene(text);
          lastAnalyzedTextRef.current = text;
          setInputText('');
        }
      } catch (err) {
        console.warn('[SmartSceneInput] Architect failed, using local analysis:', err);
        analyzeAndProcessScene(text);
        lastAnalyzedTextRef.current = text;
        setInputText('');
      } finally {
        setIsRefining(false);
        setStreamingText('');
      }
      return;
    }

    // Fall back to original image analysis for images
    await runRefine(text, { clearInputOnSuccess: true, clearImagesOnSuccess: true });
  }, [inputText, images.length, architectAnalyzeDirect, analyzeAndProcessScene, runRefine, onSceneChange]);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    setIsRefining(false);
    setStreamingText('');
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleRefine();
    }
  };

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (!files.length) return;

    const maxSize = 10 * 1024 * 1024;
    let added = false;
    for (const file of Array.from(files)) {
      if (images.length >= 4) break;
      if (file.size > maxSize || !file.type.startsWith('image/')) continue;
      try {
        const { base64, mimeType } = await fileToBase64(file);
        const preview = URL.createObjectURL(file);
        setImages(prev => [...prev, { file, preview, base64, mimeType }]);
        added = true;
      } catch { /* skip */ }
    }

    if (added && !inputText.trim()) {
      // kick off refinement to interpret the dropped image
      runRefine('', { clearInputOnSuccess: false, clearImagesOnSuccess: false }).catch(() => {});
    }
  }, [images.length, inputText, runRefine]);

  const handleExampleClick = (example: string) => {
    setInputText(example);
    setShowExamples(false);
    textareaRef.current?.focus();
  };

  const liveElementChips: Array<{ label: string; value: string }> = liveAnalysis ? [
    { label: 'Subject', value: liveAnalysis.elements.subject },
    { label: 'Action', value: liveAnalysis.elements.action },
    { label: 'Environment', value: liveAnalysis.elements.environment },
    { label: 'Time/Lighting', value: liveAnalysis.elements.timeLighting },
    { label: 'Mood/Style', value: liveAnalysis.elements.moodStyle },
  ].filter((item) => item.value.trim().length > 0) : [];

  const handleApplyArchitectPrompt = useCallback((prompt: string) => {
    onSceneChange(prompt);
    setInputText('');
  }, [onSceneChange]);

  return (
    <div className="smart-scene-input space-y-2">
      {/* Compact ChatGPT-style Input */}
      <motion.div
        className={`relative rounded-xl border transition-all duration-300 overflow-hidden ${
          isDragOver
            ? 'border-primary/60 bg-primary/5'
            : 'border-border/40 bg-card/60 hover:border-border/60'
        }`}
        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        {/* Image previews - compact row */}
        <AnimatePresence>
          {images.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex gap-1.5 px-2.5 pt-2 overflow-x-auto"
            >
              {images.map((img, i) => (
                <motion.div
                  key={img.preview}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="relative shrink-0 group"
                >
                  <img
                    src={img.preview}
                    alt={`Reference ${i + 1}`}
                    className="w-12 h-12 rounded-lg object-cover border border-border/30"
                  />
                  <button
                    onClick={() => removeImage(i)}
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X className="w-2.5 h-2.5 text-destructive-foreground" />
                  </button>
                </motion.div>
              ))}
              {images.length < 4 && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-12 h-12 rounded-lg border border-dashed border-border/30 flex items-center justify-center hover:border-primary/40 transition-all shrink-0"
                >
                  <ImagePlus className="w-3.5 h-3.5 text-muted-foreground/30" />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Single-line input with inline actions */}
        <div className="flex items-center gap-1.5 px-2.5 py-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isRefining || images.length >= 4}
            className="p-1 rounded-md hover:bg-muted/50 transition-colors disabled:opacity-30 shrink-0"
            title="Upload reference images"
          >
            <ImagePlus className="w-3.5 h-3.5 text-muted-foreground/40" />
          </button>
          <button
            onClick={() => setShowExamples(!showExamples)}
            className="p-1 rounded-md hover:bg-muted/50 transition-colors shrink-0"
            title="Examples"
          >
            <Wand2 className="w-3.5 h-3.5 text-muted-foreground/40" />
          </button>

          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={e => {
              setInputText(e.target.value);
              // Don't push raw text to sceneDescription anymore
              // Only the refined/translated result goes to the prompt
            }}
            onKeyDown={handleKeyDown}
            placeholder={currentPromptRef.current ? "Add details, change lighting, modify style..." : "A girl in neon rain... بنت في مشهد سينمائي..."}
            className="flex-1 bg-transparent text-sm text-foreground/90 outline-none placeholder:text-muted-foreground/35 resize-none leading-relaxed min-h-[28px] max-h-[90px]"
            disabled={isRefining}
            rows={1}
          />



          {isRefining ? (
            <div className="flex items-center gap-1 shrink-0">
              <div className="flex gap-0.5">
                <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: 0 }} className="w-1 h-1 rounded-full bg-primary" />
                <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: 0.2 }} className="w-1 h-1 rounded-full bg-primary" />
                <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }} className="w-1 h-1 rounded-full bg-primary" />
              </div>
              <button onClick={handleCancel} className="p-1 rounded-md hover:bg-destructive/10 transition-colors">
                <X className="w-3.5 h-3.5 text-destructive/50" />
              </button>
            </div>
          ) : (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleRefine}
              disabled={!inputText.trim() && images.length === 0}
              className="px-4 py-2 rounded-xl bg-primary/90 text-primary-foreground hover:bg-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed shrink-0 flex items-center gap-1.5 text-sm"
            >
              {isArchitectProcessing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Analyzing...
                </>
              ) : architectResult?.prompt && inputText.trim() ? (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  Apply
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  Generate
                </>
              )}
            </motion.button>
          )}
        </div>

      </motion.div>

      {/* Examples dropdown - compact */}
      <AnimatePresence>
        {showExamples && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap gap-1 pt-1.5">
              {EXAMPLE_PROMPTS.map((example, i) => (
                <button
                  key={i}
                  onClick={() => handleExampleClick(example)}
                  className="px-2 py-1 rounded-md border border-border/20 bg-card/30 text-[9px] text-muted-foreground/50 hover:text-foreground hover:border-primary/30 transition-all truncate max-w-[200px]"
                >
                  {example}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Streaming preview - compact */}
      <AnimatePresence>
        {streamingText && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden pt-1"
          >
            <div className="rounded-lg border border-primary/15 bg-primary/5 px-2.5 py-1.5 text-[10px] font-mono text-foreground/60 leading-relaxed">
              <span className="text-primary/50 mr-1">✦</span>
              {streamingText}
              <span className="inline-block w-1.5 h-3 bg-primary/50 animate-pulse ml-0.5 align-middle rounded-sm" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Prompt Architect analysis runs in background - UI hidden for clean experience */}
    </div>
  );
}
